import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChartLine, FaArrowUp, FaArrowDown, FaExchangeAlt, FaShieldAlt, FaClock } from 'react-icons/fa';
import { io } from 'socket.io-client';
import { usePrices } from '../../../context/PriceContext';

// --- Black-Scholes Utility Functions ---
const erf = (x) => {
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);
    const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
    const t = 1 / (1 + p * x);
    return sign * (1 - (((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x)));
};

const normCdf = (x) => 0.5 * (1 + erf(x / Math.sqrt(2)));

const calculateBS = (spot, strike, expiryDate, type, sigma = 0.585, r = 0.05) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const secs = (expiry - now) / 1000;

    if (secs <= 0) {
        return type === "CE" ? Math.max(spot - strike, 0) : Math.max(strike - spot, 0);
    }

    const T = secs / (365 * 24 * 3600);
    const S = 1;
    const K = strike / spot;

    const d1 = (Math.log(S / K) + (r + 0.5 * sigma ** 2) * T) / (sigma * Math.sqrt(T));
    const d2 = d1 - sigma * Math.sqrt(T);

    const price = type === "CE"
        ? S * normCdf(d1) - K * Math.exp(-r * T) * normCdf(d2)
        : K * Math.exp(-r * T) * normCdf(-d2) - S * normCdf(-d1);

    return parseFloat((price * spot).toFixed(2));
};

const extractPrice = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return parseFloat(val);
    if (Array.isArray(val)) return parseFloat(val[5] || val[0] || 0); 
    if (typeof val === 'object' && val !== null) {
        return parseFloat(val.lp || val.price || val.last_price || val.mark_price || val.data || 0);
    }
    return 0;
};

const PriceFlash = ({ value, prefix = "$" }) => {
    const numericValue = extractPrice(value);
    const [prevValue, setPrevValue] = useState(numericValue);
    const [flash, setFlash] = useState('');

    useEffect(() => {
        if (numericValue > prevValue) setFlash('flash-green');
        else if (numericValue < prevValue) setFlash('flash-red');
        
        if (numericValue !== prevValue) {
            const timer = setTimeout(() => setFlash(''), 1000);
            setPrevValue(numericValue);
            return () => clearTimeout(timer);
        }
    }, [numericValue]);

    return (
        <span className={`price-text ${flash}`}>
            {prefix}{numericValue?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
    );
};

const BrokerOptionsChain = () => {
    const [broker, setBroker] = useState('delta'); // delta, coindcx, coinswitch
    const [asset, setAsset] = useState('BTC');
    const [data, setData] = useState(null);
    const [selectedExpiry, setSelectedExpiry] = useState(null);
    const [loading, setLoading] = useState(true);
    
    const prices = usePrices();
    const livePriceFromAppSocket = asset === 'BTC' ? prices['BTCUSD'] : prices['ETHUSD'];

    const TRADE_URL = window.location.hostname === "localhost" 
        ? "http://localhost:8000" 
        : `${window.location.protocol}//${window.location.hostname}:8000`;

    // Calculate Expiry Dates
    const expiries = useMemo(() => {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        const nextWeek = new Date(today);
        nextWeek.setDate(today.getDate() + 7);
        const monthly = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return [today, tomorrow, nextWeek, monthly];
    }, []);

    useEffect(() => {
        if (!selectedExpiry) setSelectedExpiry(expiries[0]);
    }, [expiries]);

    useEffect(() => {
        const tradeSocket = io(TRADE_URL);
        const eventName = broker === 'delta' ? 'options_chain_update' : `${broker}_update`;
        const initEvent = broker === 'delta' ? 'get_chain' : `get_${broker}_data`;

        tradeSocket.on('connect', () => {
            tradeSocket.emit(initEvent, asset);
        });

        tradeSocket.on(eventName, (update) => {
            if (update.asset === asset) {
                setData(update);
                setLoading(false);
            }
        });

        return () => tradeSocket.disconnect();
    }, [asset, broker]);

    // Calculate Theoretical Chain for Non-Delta Brokers
    const simulatedChain = useMemo(() => {
        const spot = extractPrice(data?.last_price || livePriceFromAppSocket);
        if (!spot || broker === 'delta') return data?.options_chain || [];

        const step = asset === 'BTC' ? 500 : 50;
        const atm = Math.round(spot / step) * step;
        const chain = [];
        for (let i = -5; i <= 5; i++) {
            const strike = atm + (i * step);
            chain.push({
                strike,
                call: { symbol: `C-${asset}-${strike}`, price: calculateBS(spot, strike, selectedExpiry, "CE") },
                put: { symbol: `P-${asset}-${strike}`, price: calculateBS(spot, strike, selectedExpiry, "PE") }
            });
        }
        return chain;
    }, [data, livePriceFromAppSocket, broker, asset, selectedExpiry]);

    const spotPrice = extractPrice(data?.last_price || livePriceFromAppSocket);

    return (
        <motion.div 
            className="container-fluid py-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            style={{ background: '#020617', minHeight: '100vh', color: '#ffffff', fontFamily: "'Inter', sans-serif" }}
        >
            {/* Broker & Asset Selection */}
            <div className="row mb-4 g-3 align-items-center">
                <div className="col-md-4">
                    <h2 className="fw-bold mb-0 d-flex align-items-center text-white">
                        <FaExchangeAlt className="me-2 text-primary" /> Multi-Broker Option Chain
                    </h2>
                </div>
                <div className="col-md-8 d-flex justify-content-md-end gap-3">
                    <div className="btn-group p-1" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '12px' }}>
                        {['delta', 'coindcx', 'coinswitch'].map(b => (
                            <button key={b} className={`btn btn-sm px-3 text-uppercase ${broker === b ? 'btn-primary shadow' : 'text-white'}`} onClick={() => setBroker(b)} style={{ borderRadius: '10px' }}>
                                {b}
                            </button>
                        ))}
                    </div>
                    <div className="btn-group p-1" style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '12px' }}>
                        {['BTC', 'ETH'].map(a => (
                            <button key={a} className={`btn btn-sm px-3 ${asset === a ? 'btn-primary shadow' : 'text-white'}`} onClick={() => setAsset(a)} style={{ borderRadius: '10px' }}>
                                {a}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Expiry Picker */}
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex gap-2 overflow-auto pb-2">
                        {expiries.map((date, i) => (
                            <button 
                                key={i} 
                                className={`btn btn-sm text-nowrap px-3 py-2 ${selectedExpiry?.toDateString() === date.toDateString() ? 'btn-primary' : 'btn-outline-light'}`}
                                onClick={() => setSelectedExpiry(date)}
                                style={{ borderRadius: '12px', minWidth: '100px' }}
                            >
                                <FaClock className="me-2" />
                                {date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                {i === 0 ? ' (Today)' : ''}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="row g-4 mb-4">
                <div className="col-md-4">
                    <div className="card border-0 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.8)', borderRadius: '20px' }}>
                        <div className="card-body p-4">
                            <h6 className="text-light small fw-bold text-uppercase mb-2" style={{ opacity: 0.7 }}>Live {asset} Price</h6>
                            <h2 className="mb-0 fw-bold text-white"><PriceFlash value={spotPrice} /></h2>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.8)', borderRadius: '20px' }}>
                        <div className="card-body p-4 text-center">
                            <h6 className="text-light small fw-bold text-uppercase mb-2" style={{ opacity: 0.7 }}>Source Type</h6>
                            <div className={`badge ${broker === 'delta' ? 'bg-success' : 'bg-info'} p-2 px-3 rounded-pill text-white fw-bold`}>
                                {broker === 'delta' ? 'REAL MARKET DATA' : 'THEORETICAL (BLACK-SCHOLES)'}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-4">
                    <div className="card border-0 shadow-lg" style={{ background: 'rgba(30, 41, 59, 0.8)', borderRadius: '20px' }}>
                        <div className="card-body p-4 text-end">
                            <h6 className="text-light small fw-bold text-uppercase mb-2" style={{ opacity: 0.7 }}>Risk Management</h6>
                            <div className="text-success small fw-bold"><FaShieldAlt className="me-1" /> Active (15% SL)</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-2xl" style={{ background: 'rgba(15, 23, 42, 0.8)', backdropFilter: 'blur(20px)', borderRadius: '24px', overflow: 'hidden' }}>
                <div className="table-responsive">
                    <table className="table table-borderless text-white mb-0">
                        <thead style={{ background: 'rgba(255,255,255,0.07)' }}>
                            <tr className="text-white small text-uppercase">
                                <th className="ps-4 py-3 fw-bold">Call Price</th>
                                <th className="py-3 text-center fw-bold">Strike</th>
                                <th className="pe-4 py-3 text-end fw-bold">Put Price</th>
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence mode='popLayout'>
                                {simulatedChain.map((row, idx) => (
                                    <motion.tr 
                                        key={`${row.strike}-${idx}`}
                                        initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                                        style={{ 
                                            background: Math.abs(row.strike - spotPrice) < (asset === 'BTC' ? (broker === 'delta' ? 200 : 300) : 30) ? 'rgba(37, 99, 235, 0.25)' : 'transparent',
                                            borderBottom: '1px solid rgba(255,255,255,0.05)'
                                        }}
                                    >
                                        <td className="ps-4 py-3">
                                            <div className="fw-bold text-white fs-5"><PriceFlash value={row.call.price} /></div>
                                            <div className="text-white-50" style={{ fontSize: '11px', fontWeight: '500' }}>{row.call.symbol}</div>
                                        </td>
                                        <td className="py-3 text-center">
                                            <span className="badge bg-white text-dark p-2 px-3 fw-bold shadow" style={{ borderRadius: '8px', fontSize: '14px' }}>{row.strike}</span>
                                        </td>
                                        <td className="pe-4 py-3 text-end">
                                            <div className="fw-bold text-white fs-5"><PriceFlash value={row.put.price} /></div>
                                            <div className="text-white-50" style={{ fontSize: '11px', fontWeight: '500' }}>{row.put.symbol}</div>
                                        </td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>
                </div>
            </div>

            <style>{`
                .price-text { transition: color 0.3s ease; }
                .flash-green { color: #4ade80 !important; }
                .flash-red { color: #f87171 !important; }
                .btn-outline-primary.active { background: #2563eb; color: white; }
                .table-responsive::-webkit-scrollbar { height: 4px; }
                .table-responsive::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 10px; }
            `}</style>
        </motion.div>
    );
};

export default BrokerOptionsChain;
